/**
 * Server-Sent Events over fetch(POST).
 *
 * `createSseParser` is a WHATWG-style event-stream parser: CRLF, LF and lone CR line endings (also when a
 * CRLF pair is split across chunks), multi-line `data`, `event` and `id` fields, `:` comment lines and a
 * leading BOM. `streamChat` drives it from a fetch ReadableStream with an idle timeout that comment
 * heartbeats reset, and reports every failure as a contract `error` event.
 */
import { ERROR_CODES, EVENTS, PROTOCOL } from './contract';

/**
 * @param {{onMessage: function({event: string, data: string, id: string}): void,
 *          onComment?: function(string): void}} handlers
 * @returns {{feed: function(string): void, end: function(): void}}
 */
export function createSseParser({ onMessage, onComment }) {
  let buffer = '';
  let dataLines = [];
  let eventName = '';
  let lastId = '';
  let sawBom = false;

  const dispatch = () => {
    if (dataLines.length === 0) {
      eventName = '';
      return;
    }
    const data = dataLines.join('\n');
    const event = eventName || 'message';
    dataLines = [];
    eventName = '';
    onMessage({ event, data, id: lastId });
  };

  const processLine = (line) => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line[0] === ':') {
      if (onComment) onComment(line.slice(1).trimStart());
      return;
    }
    const colon = line.indexOf(':');
    let field;
    let value;
    if (colon === -1) {
      field = line;
      value = '';
    } else {
      field = line.slice(0, colon);
      value = line.slice(colon + 1);
      if (value[0] === ' ') value = value.slice(1);
    }
    switch (field) {
      case 'data':
        dataLines.push(value);
        break;
      case 'event':
        eventName = value;
        break;
      case 'id':
        if (!value.includes('\0')) lastId = value;
        break;
      default:
        // `retry` and unknown fields are ignored: the client never reconnects a POST on its own.
        break;
    }
  };

  const feed = (chunk) => {
    if (!chunk) return;
    buffer += chunk;
    if (!sawBom) {
      if (buffer.length === 0) return;
      if (buffer.charCodeAt(0) === 0xfeff) buffer = buffer.slice(1);
      sawBom = true;
    }
    let start = 0;
    for (let i = 0; i < buffer.length; i += 1) {
      const ch = buffer[i];
      if (ch === '\n') {
        processLine(buffer.slice(start, i));
        start = i + 1;
      } else if (ch === '\r') {
        if (i === buffer.length - 1) break; // wait: the matching \n may come in the next chunk
        processLine(buffer.slice(start, i));
        if (buffer[i + 1] === '\n') i += 1;
        start = i + 1;
      }
    }
    buffer = buffer.slice(start);
  };

  const end = () => {
    // A trailing lone CR is a complete line ending at end of stream.
    if (buffer.endsWith('\r')) processLine(buffer.slice(0, -1));
    // Per spec, pending data at end of stream is discarded: an event without its terminating blank
    // line was cut mid-event.
    buffer = '';
    dataLines = [];
    eventName = '';
  };

  return { feed, end };
}

/** Parse a whole recorded stream (fixtures, tests) into payload objects. */
export function parseSseText(text) {
  const events = [];
  const parser = createSseParser({
    onMessage: ({ event, data, id }) => {
      const payload = safeJson(data);
      if (payload) events.push({ ...payload, type: payload.type || event, _id: id });
    },
  });
  parser.feed(text);
  parser.end();
  return events;
}

function safeJson(text) {
  try {
    const value = JSON.parse(text);
    return value && typeof value === 'object' ? value : null;
  } catch (e) {
    return null;
  }
}

const TERMINAL = new Set([EVENTS.DONE, EVENTS.ERROR]);

function clientError(code, message, { retryable = true, partial = false } = {}) {
  return { type: EVENTS.ERROR, code, message, retryable, partial, client: true };
}

/**
 * POST `body` to `url` and call `onEvent(payload, {id})` for every contract event.
 *
 * Resolves (never rejects) with {outcome: 'done'|'error'|'aborted'}. Network failures, HTTP errors,
 * idle timeouts and streams that end without `done`/`error` become one synthesized `error` event
 * (`client: true`). Aborting `signal` resolves with 'aborted' and emits nothing.
 *
 * @param {{url: string, body: Object, signal?: AbortSignal, onEvent: function(Object, {id: string}): void,
 *          idleTimeoutMs?: number, fetchImpl?: function, headers?: Object, messages?: Object}} options
 */
export async function streamChat({
  url,
  body,
  signal,
  onEvent,
  idleTimeoutMs = 45000,
  fetchImpl,
  headers = {},
  messages = {},
}) {
  const doFetch = fetchImpl || (typeof window !== 'undefined' && window.fetch ? window.fetch.bind(window) : null);
  const text = {
    network: messages.network || 'Connection lost. Please try again.',
    timeout: messages.timeout || 'The server stopped responding. Please try again.',
    http: messages.http || 'Unable to send your message. Please try again.',
  };

  const controller = new AbortController();
  let userAborted = false;
  let timedOut = false;
  let idleTimer = null;
  let terminal = null;
  let sawDelta = false;

  const onUserAbort = () => {
    userAborted = true;
    controller.abort();
  };
  if (signal) {
    if (signal.aborted) return { outcome: 'aborted' };
    signal.addEventListener('abort', onUserAbort);
  }

  const armIdle = () => {
    if (!idleTimeoutMs) return;
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, idleTimeoutMs);
  };

  const emit = (payload, id = '') => {
    if (terminal) return; // nothing after a terminal event
    if (payload.type === EVENTS.DELTA) sawDelta = true;
    if (TERMINAL.has(payload.type)) terminal = payload.type;
    onEvent(payload, { id });
  };

  const cleanup = () => {
    if (idleTimer) clearTimeout(idleTimer);
    if (signal) signal.removeEventListener('abort', onUserAbort);
  };

  try {
    if (!doFetch) throw new Error('fetch is not available');
    armIdle();
    const res = await doFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...headers },
      body: JSON.stringify({ protocol: PROTOCOL, ...body }),
      signal: controller.signal,
    });

    if (!res.ok) {
      let detail = '';
      try {
        const json = await res.json();
        detail = typeof json?.detail === 'string' ? json.detail : (json?.message || '');
      } catch (e) { /* not JSON */ }
      const code = res.status === 401 || res.status === 403 ? ERROR_CODES.UNAUTHORIZED
        : res.status === 422 || res.status === 400 ? ERROR_CODES.VALIDATION
          : ERROR_CODES.INTERNAL;
      emit(clientError(code, detail || text.http, { retryable: res.status >= 500 || res.status === 429 }));
      return { outcome: 'error' };
    }
    if (!res.body || typeof res.body.getReader !== 'function') {
      throw new Error('Response has no readable body');
    }

    const parser = createSseParser({
      onMessage: ({ event, data, id }) => {
        const payload = safeJson(data);
        if (!payload) return;
        if (!payload.type) payload.type = event;
        emit(payload, id);
      },
      onComment: () => { /* heartbeat: the read below already re-armed the idle timer */ },
    });

    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    armIdle();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      armIdle();
      parser.feed(decoder.decode(value, { stream: true }));
      if (terminal) break;
    }
    parser.feed(decoder.decode());
    parser.end();
    if (terminal && typeof reader.cancel === 'function') {
      reader.cancel().catch(() => {});
    }

    if (!terminal) {
      emit(clientError(ERROR_CODES.NETWORK, text.network, { partial: sawDelta }));
      return { outcome: 'error' };
    }
    return { outcome: terminal === EVENTS.DONE ? 'done' : 'error' };
  } catch (err) {
    if (userAborted) return { outcome: 'aborted' };
    if (terminal === EVENTS.DONE) return { outcome: 'done' };
    if (terminal === EVENTS.ERROR) return { outcome: 'error' };
    if (timedOut) {
      emit(clientError(ERROR_CODES.TIMEOUT, text.timeout, { partial: sawDelta }));
    } else {
      emit(clientError(ERROR_CODES.NETWORK, text.network, { partial: sawDelta }));
    }
    return { outcome: 'error' };
  } finally {
    cleanup();
  }
}
